/**
 * 날짜 유틸리티 함수
 * 모든 날짜는 로컬 시간(KST) 기준으로 처리
 */

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환 (로컬 시간 기준)
 */
export const getToday = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * 이번 달 1일을 YYYY-MM-DD 형식으로 반환 (로컬 시간 기준)
 */
export const getFirstDayOfMonth = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

/**
 * 현재 날짜/시간을 ISO 형식으로 반환 (로컬 시간 기준)
 * 예: 2025-01-15T14:30:00
 */
export const getLocalISOString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * Date 객체를 YYYY-MM-DD 형식으로 변환 (로컬 시간 기준)
 */
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Date 객체를 YYYY-MM-DDTHH:mm:ss 형식으로 변환 (로컬 시간 기준)
 */
export const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

/**
 * 타임스탬프 문자열 생성 (YYYYMMDDHHmm)
 * 파일명 등에 사용
 */
export const getTimestamp = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}${month}${day}${hours}${minutes}`;
};

/**
 * 날짜 문자열 생성 (YYYYMMDD)
 */
export const getDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
};

/**
 * N일 전/후 날짜를 YYYY-MM-DD 형식으로 반환
 * @param days - 양수: N일 후, 음수: N일 전
 */
export const getDateOffset = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatDate(date);
};

/**
 * N개월 전/후의 1일을 YYYY-MM-DD 형식으로 반환
 * @param months - 양수: N개월 후, 음수: N개월 전
 */
export const getMonthOffset = (months: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  date.setDate(1);
  return formatDate(date);
};

/**
 * 해당 월의 마지막 날짜를 YYYY-MM-DD 형식으로 반환
 */
export const getLastDayOfMonth = (year?: number, month?: number): string => {
  const now = new Date();
  const y = year ?? now.getFullYear();
  const m = month ?? now.getMonth() + 1;
  // 다음 달 0일 = 이번 달 마지막 날
  const lastDay = new Date(y, m, 0);
  return formatDate(lastDay);
};
